import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, AlertTriangle, XCircle, Activity, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface ServiceStatus {
  status: "operational" | "degraded" | "down";
  latency_ms: number;
}

interface HealthResponse {
  status: "healthy" | "degraded" | "unhealthy";
  checks: Record<string, ServiceStatus>;
  timestamp: string;
}

const SERVICE_LABELS: Record<string, string> = {
  db: "Banco de Dados",
  auth: "Autenticação",
  edge: "Backend Functions",
  evolution_api: "WhatsApp API",
  lovable_gateway: "IA Gateway",
  stripe: "Pagamentos",
};

const statusConfig = {
  operational: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-500/10", label: "Operacional" },
  degraded: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-500/10", label: "Degradado" },
  down: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/10", label: "Indisponível" },
};

export default function Status() {
  const { data: health, isLoading, refetch, isFetching } = useQuery<HealthResponse>({
    queryKey: ["public-health-check"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("health-check");
      if (error) throw error;
      return data as HealthResponse;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const { data: snapshots } = useQuery({
    queryKey: ["status-snapshots"],
    queryFn: async () => {
      const { data } = await supabase
        .from("status_snapshots")
        .select("overall_status, uptime_24h, checked_at")
        .order("checked_at", { ascending: false })
        .limit(1);
      return data?.[0] || null;
    },
    staleTime: 60_000,
  });

  const uptime = snapshots?.uptime_24h ?? 100;
  const overallStatus = health?.status || "healthy";

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">SYRA Status</h1>
              <p className="text-sm text-muted-foreground">Status dos serviços em tempo real</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>

        {/* Overall Status Banner */}
        <Card className={`mb-8 border-2 ${
          overallStatus === "healthy" ? "border-emerald-500/30" :
          overallStatus === "degraded" ? "border-amber-500/30" : "border-red-500/30"
        }`}>
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {overallStatus === "healthy" ? (
                  <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                ) : overallStatus === "degraded" ? (
                  <AlertTriangle className="w-8 h-8 text-amber-500" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-500" />
                )}
                <div>
                  <p className="text-lg font-semibold">
                    {overallStatus === "healthy" ? "Todos os sistemas operacionais" :
                     overallStatus === "degraded" ? "Alguns serviços degradados" :
                     "Problemas detectados"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Uptime 24h: {uptime}%
                    {health?.timestamp && ` · Última verificação: ${new Date(health.timestamp).toLocaleTimeString("pt-BR")}`}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Cards */}
        {isLoading ? (
          <p className="text-center text-muted-foreground py-8">Verificando serviços...</p>
        ) : health?.checks ? (
          <div className="space-y-3">
            {Object.entries(health.checks).map(([key, svc]) => {
              const cfg = statusConfig[svc.status];
              const Icon = cfg.icon;
              return (
                <Card key={key}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${cfg.bg}`}>
                          <Icon className={`w-4 h-4 ${cfg.color}`} />
                        </div>
                        <span className="font-medium">{SERVICE_LABELS[key] || key}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {svc.latency_ms > 0 && (
                          <span className="text-xs text-muted-foreground">{svc.latency_ms}ms</span>
                        )}
                        <Badge variant="outline" className={cfg.color}>
                          {cfg.label}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : null}

        {/* Footer */}
        <div className="mt-12 text-center text-xs text-muted-foreground">
          <p>Atualizado automaticamente a cada 60 segundos</p>
          <p className="mt-1">SYRA · Status Page</p>
        </div>
      </div>
    </div>
  );
}
